import { LOCAL_STORAGE_KEYS } from '@/lib/constants'
import { getApiUrl } from '@/lib/helpers/apiHelpers'
import {
  isOfficeAddIn,
  isOfficeParent,
  isOfficeUI,
} from '@/lib/helpers/officeHelpers'
import { ROUTES } from '@/lib/routes'
import { SEARCH_PARAMS } from '@/lib/searchParams'
import apiService from '@/lib/services/apiService'
import { UserType } from '@/types/misc'

export interface AuthData {
  isAuthenticated: boolean
}

export interface AuthSessionExtended {
  secondsRemaining: number
}

export interface AuthSessionIsAuthenticated {
  status: boolean
}

class AuthService extends EventTarget {
  private endpoint = `${getApiUrl()}/auth`

  public dispatchSessionExtended(secondsRemaining: number) {
    this.dispatchEvent(
      new CustomEvent<AuthSessionExtended>('sessionExtended', {
        detail: { secondsRemaining },
      }),
    )
  }

  public dispatchSessionIsAuthenticated(status: boolean) {
    this.dispatchEvent(
      new CustomEvent<AuthSessionIsAuthenticated>('sessionIsAuthenticated', {
        detail: { status },
      }),
    )
  }

  // Fired when the API reports the session is now acting as a different
  // user/scope/company than the frontend was initialised with, so the UI can
  // warn the user before re-bootstrapping against the new context.
  public dispatchSessionContextConflict() {
    this.dispatchEvent(new Event('sessionContextConflict'))
  }

  async initialize(): Promise<AuthData> {
    // Check if user is authenticated
    try {
      const response = await apiService.get(`${this.endpoint}/status`)

      const isAuthenticated = response.data.isAuthenticated === true

      if (isAuthenticated) {
        if (isOfficeUI() && !isOfficeParent()) {
          const message = JSON.stringify({ isAuthenticated: true })
          Office.context.ui.messageParent(message)
        }
      }

      return { isAuthenticated }
    } catch (error) {
      console.error('Failed to check API cookie authentication status:', error)
      return { isAuthenticated: false }
    }
  }

  async refreshToken() {
    try {
      // Make any request to refresh the session, apiService will re-read and process the refresh expiry header
      await apiService.get(`${this.endpoint}/status`)
    } catch (error) {
      console.error('Failed to refresh', error)
      // If refresh fails, redirect to log in
      await this.logout()
    }
  }

  registerAsCompanyUser(redirectUri?: string) {
    window.location.href = `${this.endpoint}/register?redirect_uri=${encodeURIComponent(redirectUri ? redirectUri : window.location.href)}`
  }

  signInAsCompanyUser(redirectUri?: string) {
    this.signIn(redirectUri || this.getLoginRedirectUri())
  }

  signInAsClientUser(options: Partial<typeof SEARCH_PARAMS.compassRedirect>) {
    const redirectUri =
      window.location.origin + ROUTES.compassRedirect({ searchParams: options })
    this.signIn(redirectUri)
  }

  signIn(redirectUri?: string) {
    let postLoginRedirectUri = redirectUri || this.getLoginRedirectUri()
    if (isOfficeAddIn()) {
      postLoginRedirectUri += '#officeAddIn'
    }
    window.location.href = `${this.endpoint}/login?redirect_uri=${encodeURIComponent(postLoginRedirectUri)}`
  }

  registerAsClientUser(options: Partial<typeof SEARCH_PARAMS.compassRedirect>) {
    const redirectUri =
      window.location.origin + ROUTES.compassRedirect({ searchParams: options })
    window.location.href = `${this.endpoint}/register?redirect_uri=${encodeURIComponent(redirectUri)}`
  }

  getLoginRedirectUri(): string {
    return `${window.location.origin}${ROUTES.index()}`
  }

  async logout(redirectUri?: string) {
    await apiService.post(`${this.endpoint}/logout`)

    window.location.href = redirectUri || this.getLoginRedirectUri()
  }

  getAccountManagementUrl() {
    return (
      import.meta.env.VITE_KEYCLOAK_URL +
      '/realms/' +
      import.meta.env.VITE_KEYCLOAK_REALM +
      '/account/#/'
    )
  }

  getPreviouslyUserSelectedScope(): string | undefined {
    return (
      window.localStorage.getItem(LOCAL_STORAGE_KEYS.previouslySelectedScope) ||
      undefined
    )
  }

  setUserSelectedScope(scope: UserType) {
    window.localStorage.setItem(
      LOCAL_STORAGE_KEYS.previouslySelectedScope,
      scope,
    )
  }

  getPreviouslyUserSelectedCompanyId(): number | undefined {
    const previouslySelectedCompanyId = window.localStorage.getItem(
      LOCAL_STORAGE_KEYS.previouslySelectedCompanyId,
    )

    return previouslySelectedCompanyId
      ? Number(previouslySelectedCompanyId)
      : undefined
  }

  setUserSelectedCompanyId(companyId: number) {
    window.localStorage.setItem(
      LOCAL_STORAGE_KEYS.previouslySelectedCompanyId,
      companyId.toString(),
    )
  }

  hasCurrentUserType(): boolean {
    return !!window.RQ.store?.session?.metadata?.currentUserType
  }
}

const authService = new AuthService()

export default authService
